"use strict";

import {
  Alert,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import Loader from "../../Components/Loader";
import { Store } from "../../Store";
import { getLipSyncStatus } from "../../features/projectWork/api";
import { useProjectMediaPlayback } from "../../hooks/useProjectMediaPlayback";

const ACTIVE_STATUSES = new Set(["queued", "submitted", "running"]);
const FAILED_STATUSES = new Set([
  "failed_retryable",
  "failed_permanent",
  "review_required",
]);
const EMPTY_LIP_SYNC_VERSIONS = [];

const versionKey = (version) =>
  String(version?.uniqueId || version?.runId || version?.path || "");

const LipSyncOutputScreen = () => {
  const { language: languageCode } = useParams();
  const { state } = useContext(Store);
  const { projectFile } = state;
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedVersionId, setSelectedVersionId] = useState("");

  const loadRun = useCallback(async () => {
    if (!projectFile?.id || !languageCode) {
      return;
    }
    try {
      const response = await getLipSyncStatus({
        projectId: projectFile.id,
        languageCode,
      });
      setRun(response.data);
      setError("");
    } catch (requestError) {
      if (requestError.response?.status === 404) {
        setRun(null);
        setError("No lip-sync output is available for this language yet.");
      } else {
        const detail =
          requestError.normalized?.message ||
          requestError.response?.data?.message ||
          requestError.response?.data?.error;
        setError(
          detail
            ? `The lip-sync status could not be loaded: ${detail}`
            : "The lip-sync status could not be loaded. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  }, [languageCode, projectFile?.id]);

  useEffect(() => {
    loadRun();
  }, [loadRun]);

  useEffect(() => {
    if (!run || !ACTIVE_STATUSES.has(run.status)) {
      return undefined;
    }
    const timer = window.setInterval(loadRun, 15000);
    return () => window.clearInterval(timer);
  }, [loadRun, run]);

  const lipSyncVersions = Array.isArray(run?.lipSyncList)
    ? run.lipSyncList
    : EMPTY_LIP_SYNC_VERSIONS;

  useEffect(() => {
    if (lipSyncVersions.length === 0) {
      setSelectedVersionId("");
      return;
    }
    setSelectedVersionId((current) =>
      lipSyncVersions.some((version) => versionKey(version) === current)
        ? current
        : versionKey(lipSyncVersions[lipSyncVersions.length - 1])
    );
  }, [lipSyncVersions]);

  const selectedVersion =
    lipSyncVersions.find(
      (version) => versionKey(version) === selectedVersionId
    ) || lipSyncVersions[lipSyncVersions.length - 1];
  const selectedVideoObjectName =
    selectedVersion?.path || run?.videoObjectName || "";

  const { playbackUrl, playbackUnavailable } = useProjectMediaPlayback({
    reference: selectedVideoObjectName,
    projectId: projectFile?.id,
  });

  if (loading) {
    return <Loader Load />;
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 900, mx: "auto", mt: 4 }}>
        <Alert severity="info">{error}</Alert>
      </Box>
    );
  }

  if (run && ACTIVE_STATUSES.has(run.status) && lipSyncVersions.length === 0) {
    return (
      <Grid container justifyContent="center" sx={{ mt: 4 }}>
        <Typography>Lip-sync is processing. This page updates automatically.</Typography>
      </Grid>
    );
  }

  if (run && FAILED_STATUSES.has(run.status) && lipSyncVersions.length === 0) {
    return (
      <Box sx={{ maxWidth: 900, mx: "auto", mt: 4 }}>
        <Alert severity="error">
          {run.error?.message || "Lip-sync did not produce an output video."}
        </Alert>
      </Box>
    );
  }

  if (!selectedVideoObjectName || playbackUnavailable || !playbackUrl) {
    return (
      <Box sx={{ maxWidth: 900, mx: "auto", mt: 4 }}>
        <Alert severity="warning">The output video is not available yet.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "min(1200px, 96vw)", mx: "auto", mt: 3 }}>
      {ACTIVE_STATUSES.has(run?.status) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          A new lip-sync version is processing. You can review an earlier
          version while it finishes.
        </Alert>
      )}
      {FAILED_STATUSES.has(run?.status) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {run.error?.message ||
            "The latest lip-sync run failed. Showing a previous version."}
        </Alert>
      )}
      {lipSyncVersions.length > 0 && (
        <FormControl sx={{ minWidth: 220, mb: 2 }}>
          <InputLabel id="lip-sync-version-label">Version</InputLabel>
          <Select
            labelId="lip-sync-version-label"
            label="Version"
            value={versionKey(selectedVersion)}
            onChange={(event) => setSelectedVersionId(event.target.value)}
          >
            {lipSyncVersions.map((version, index) => (
              <MenuItem key={versionKey(version)} value={versionKey(version)}>
                {`Version ${version.version || index + 1}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <video
        key={selectedVideoObjectName}
        controls
        src={playbackUrl}
        style={{ display: "block", width: "100%", maxHeight: "78vh" }}
      >
        Your browser does not support video playback.
      </video>
    </Box>
  );
};

export default LipSyncOutputScreen;
